import { ALLOWED_REDIRECT_DOMAINS } from '@/infra/constants/domains';

/** A placeholder origin to resolve a path against. Never navigated to. */
const PATH_CHECK_ORIGIN = 'https://redirect-check.invalid';

/**
 * Whether a path stays on the site it is resolved against. Checking the prefix is not enough: the URL
 * parser reads `//host`, `/\host` and `/<tab>/host` as a new host, and `router.push` follows them
 * off-site (Vinicius, PR #3127). Resolving and comparing the origin covers every spelling.
 */
function isSameSitePath(path: string): boolean {
  try {
    return new URL(path, PATH_CHECK_ORIGIN).origin === PATH_CHECK_ORIGIN;
  } catch {
    return false;
  }
}

/**
 * Validates a redirect URL to prevent open redirect vulnerabilities.
 * Only allows relative paths or URLs from allowed domains.
 *
 * @param url - The URL to validate (can be relative or absolute)
 * @param fallback - The fallback URL to return if validation fails (default: '/home'). If null, returns null on validation failure.
 * @returns The validated URL, the fallback if validation fails, or null if no fallback provided and validation fails
 */
export function validateRedirectUrl(url: string | null | undefined, fallback: string): string;
export function validateRedirectUrl(url: string | null | undefined, fallback: null): string | null;
export function validateRedirectUrl(url: string | null | undefined, fallback: string | null = '/home'): string | null {
  // If no URL provided, return fallback (or null if fallback is null)
  if (!url) {
    return fallback ?? null;
  }

  // Trim whitespace
  const trimmedUrl = url.trim();

  // A relative path is safe as long as it resolves on this site (not `//example.com`, `/\example.com`)
  if (trimmedUrl.startsWith('/')) {
    return isSameSitePath(trimmedUrl) ? trimmedUrl : (fallback ?? null);
  }

  // For absolute URLs, check if domain is in allowed list
  try {
    const urlObj = new URL(trimmedUrl);

    // An absolute URL back to this same site is as safe as a relative path, and becomes one. Clerk's
    // `redirectToSignIn` writes the full URL, so on a host outside the list below (a Vercel preview)
    // a signed-out visitor's link was rejected and they lost it on the way through sign-in and
    // sign-up (Mateus, PR #3127). Production's host is already listed, so nothing changes there.
    // The path is checked again: `https://app.extern.com//evil.example` has the path `//evil.example`.
    if (typeof window !== 'undefined' && urlObj.origin === window.location.origin) {
      const path = `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
      return isSameSitePath(path) ? path : (fallback ?? null);
    }

    // Get allowed domains from constants
    const allowedDomains = ALLOWED_REDIRECT_DOMAINS;

    // Check if the hostname matches any allowed domain
    const isAllowed = allowedDomains.some((domain) => {
      // Handle wildcard patterns (e.g., *.vercel.app)
      if (domain.startsWith('*.')) {
        const baseDomain = domain.slice(2); // Remove '*.'
        // Match any subdomain of the base domain (e.g., my-app.vercel.app matches *.vercel.app)
        // But not the base domain itself (vercel.app does NOT match *.vercel.app)
        if (urlObj.hostname.endsWith(`.${baseDomain}`)) {
          return true;
        }
        return false;
      }

      // Exact match
      if (urlObj.hostname === domain) {
        return true;
      }

      // Subdomain match (e.g., app.example.com matches example.com)
      if (urlObj.hostname.endsWith(`.${domain}`)) {
        return true;
      }

      return false;
    });

    if (isAllowed) {
      return trimmedUrl;
    }

    // If not allowed, return fallback (or null if fallback is null)
    return fallback ?? null;
  } catch {
    // If URL parsing fails, treat as invalid and return fallback (or null if fallback is null)
    return fallback ?? null;
  }
}
